import { useState } from 'react';
import { Text, TextInput, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import Callout from '@/components/General/Callout';
import SheetModal from '@/components/General/SheetModal';
import SolidButton from '@/components/General/SolidButton';
import { Mutation } from '@/utils/react-query/Mutations';
import { parseTimeFrame } from '@/utils/dater';

/**
 * ContactWarningBanner — prominent "Requested No Contact" surface (spec
 * §9.4 Warn, Don't Block). Warning-only: never blocks logging, tasks, or
 * automations. Resolution is an explicit, audited manager action.
 */
const ContactWarningBanner = ({ organizationID, personID, contactWarning, canManage }) => {
    const [resolveOpen, setResolveOpen] = useState(false);
    const [resolution, setResolution] = useState('');

    const resolveMutation = Mutation.followUp.resolveContactWarning({
        onSuccess: (data) => {
            if (data?.resolved) {
                Toast.show({ type: 'success', text2: 'Contact warning resolved' });
            }
            setResolveOpen(false);
            setResolution('');
        },
        onError: (error) => {
            if (!error?.expected) Toast.show({ type: 'error', text2: 'Failed to resolve the warning.' });
        },
    });

    if (!contactWarning || contactWarning.resolvedAt) return null;

    const recordedAt = contactWarning.recordedAt ? parseTimeFrame(contactWarning.recordedAt) : null;

    return (
        <>
            <Callout
                testID="follow-up-contact-warning"
                type="danger"
                title="Requested No Contact"
                message={`This person asked not to be contacted${recordedAt ? ` (${recordedAt})` : ''}. The warning stays active until a Follow Up manager resolves it.`}
            >
                {canManage && (
                    <View style={styles.actionRow}>
                        <SolidButton
                            testID="follow-up-resolve-warning"
                            size="sm"
                            variant="outlined"
                            type="error"
                            text="Resolve Warning"
                            onPress={() => setResolveOpen(true)}
                        />
                    </View>
                )}
            </Callout>

            <SheetModal
                isOpen={resolveOpen}
                title="Resolve Contact Warning"
                cancelText="Cancel"
                confirmText="Resolve"
                onCancel={() => { setResolveOpen(false); setResolution(''); }}
                onClose={() => { setResolveOpen(false); setResolution(''); }}
                onConfirm={() => resolveMutation.mutate({
                    organizationID,
                    personID,
                    resolution: resolution.trim() || 'Resolved by manager',
                })}
                loading={resolveMutation.isPending}
            >
                <View style={styles.sheetBody}>
                    <Text style={styles.sheetText}>
                        Confirm the no-contact request has been addressed. This is recorded in the audit history.
                    </Text>
                    <TextInput
                        testID="resolve-warning-notes-input"
                        style={styles.input}
                        multiline
                        numberOfLines={3}
                        placeholder="Resolution notes (optional)..."
                        placeholderTextColor="#999"
                        value={resolution}
                        onChangeText={setResolution}
                    />
                </View>
            </SheetModal>
        </>
    );
};

const styles = StyleSheet.create(({ colors }) => ({
    actionRow: { marginTop: 8, alignItems: 'flex-start' },
    sheetBody: { paddingBottom: 16, gap: 10 },
    sheetText: { fontSize: 13, color: colors.textSecondary, lineHeight: 19 },
    input: {
        fontSize: 14,
        color: colors.text,
        backgroundColor: colors.inputBackground,
        borderRadius: 10,
        padding: 12,
        textAlignVertical: 'top',
        minHeight: 72,
        _web: { outlineStyle: 'none' },
    },
}));

export default ContactWarningBanner;
